// Favorited events, no login required (Lisa's spec, 8/19). A star in the
// event popup remembers itself on THIS device via localStorage rather than a
// cookie - same no-login intent, but it survives longer and never makes a
// server round trip.
//
// Module-level state + useSyncExternalStore, not per-component useState: the
// popup's star, a card's small indicator, and the drawer's count all read the
// SAME set and have to re-render together the instant any one of them changes
// it, in this tab, with no reload.

import { useCallback, useSyncExternalStore } from 'react'
import { track } from './analytics'

const STORAGE_KEY = 'lyh_favorites_v1'

/**
 * The sentinel that stands in for "Favorited Events" inside the same `selected`
 * Set the real event-type checkboxes use (App.tsx / FilterDrawer). Wrapped in
 * double underscores because every real host is a plain human category name
 * ("Live Music", "Comedy") - this can never collide with one. slugify() turns
 * it into the readable `?hosts=favorited` on the way into the URL.
 */
export const FAVORITES_KIND = '__favorited__'

function readStore(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set()
  } catch {
    return new Set() // storage blocked (private mode) or malformed -> nobody's starred, not a crash
  }
}

let favorites = readStore()
const listeners = new Set<() => void>()

function persist() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...favorites]))
  } catch {
    // Storage blocked (private mode / full). The star still works for this
    // page load; it just won't be there on the next visit.
  }
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => listeners.delete(onChange)
}

function getSnapshot(): Set<string> {
  return favorites
}

/** True while `id` is starred. Non-reactive - for one-off reads outside React. */
export function isFavorited(id: string): boolean {
  return favorites.has(id)
}

/**
 * The favorites set, live: every component that calls this hook re-renders the
 * instant any of them toggles a star, no reload required.
 */
export function useFavorites(): {
  favorites: Set<string>
  isFavorited: (id: string) => boolean
  toggleFavorite: (id: string, eventName?: string | null) => void
} {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const toggleFavorite = useCallback((id: string, eventName?: string | null) => {
    const next = new Set(favorites)
    const selecting = !next.has(id)
    if (selecting) next.add(id)
    else next.delete(id)
    favorites = next
    persist()
    for (const l of listeners) l()
    track(selecting ? 'event_favorited' : 'event_unfavorited', {
      event_id: id,
      event_name: eventName,
    })
  }, [])

  return {
    favorites: snapshot,
    isFavorited: (id: string) => snapshot.has(id),
    toggleFavorite,
  }
}
